import { ApiError } from './apiErrors';

export interface RetryOptions {
  maxRetries?: number;
  initialDelay?: number;
  maxDelay?: number;
  backoffMultiplier?: number;
  shouldRetry?: (error: ApiError, attempt: number) => boolean;
  onRetry?: (error: ApiError, attempt: number) => void;
}

const DEFAULT_RETRY_OPTIONS: Required<RetryOptions> = {
  maxRetries: 3,
  initialDelay: 1000,
  maxDelay: 10000,
  backoffMultiplier: 2,
  shouldRetry: (error: ApiError) => error.isRetryable(),
  onRetry: () => {}
};

export class RetryHandler {
  private options: Required<RetryOptions>;
  private abortController?: AbortController;

  constructor(options?: RetryOptions) {
    this.options = { ...DEFAULT_RETRY_OPTIONS, ...options };
  }

  async execute<T>(
    operation: (signal?: AbortSignal) => Promise<T>,
    customOptions?: RetryOptions
  ): Promise<T> {
    const options = { ...this.options, ...customOptions };
    let lastError: ApiError | null = null;
    
    this.abortController = new AbortController();

    for (let attempt = 1; attempt <= options.maxRetries; attempt++) {
      try {
        const result = await operation(this.abortController.signal);
        return result;
      } catch (error) {
        lastError = error instanceof ApiError ? error : new ApiError(
          error instanceof Error ? error.message : String(error)
        );
        
        // Check if we should retry
        if (!options.shouldRetry(lastError, attempt) || attempt === options.maxRetries) {
          throw lastError;
        }

        // Check if operation was aborted
        if (this.abortController.signal.aborted) {
          throw new ApiError('İşlem iptal edildi', -3);
        }

        // Calculate delay with exponential backoff
        const delay = Math.min(
          options.initialDelay * Math.pow(options.backoffMultiplier, attempt - 1),
          options.maxDelay
        );

        // Notify retry handler
        options.onRetry(lastError, attempt);

        // Wait before retrying
        await this.delay(delay);
      }
    }

    throw lastError!;
  }

  abort(): void {
    this.abortController?.abort();
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// Hook for using retry handler with React
import { useState, useCallback, useRef } from 'react';

export interface UseRetryResult<T> {
  data: T | null;
  error: ApiError | null;
  isLoading: boolean;
  isRetrying: boolean;
  retryCount: number;
  execute: (operation: (signal?: AbortSignal) => Promise<T>) => Promise<T | null>;
  retry: () => Promise<T | null>;
  abort: () => void;
  reset: () => void;
}

export function useRetry<T>(options?: RetryOptions): UseRetryResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  
  const retryHandlerRef = useRef<RetryHandler | null>(null);
  const lastOperationRef = useRef<((signal?: AbortSignal) => Promise<T>) | null>(null);

  const execute = useCallback(async (
    operation: (signal?: AbortSignal) => Promise<T>
  ): Promise<T | null> => {
    setIsLoading(true);
    setError(null);
    setRetryCount(0);
    lastOperationRef.current = operation;

    const handler = new RetryHandler({
      ...options,
      onRetry: (error, attempt) => {
        setIsRetrying(true);
        setRetryCount(attempt);
        options?.onRetry?.(error, attempt);
      }
    });
    
    retryHandlerRef.current = handler;

    try {
      const result = await handler.execute(operation);
      setData(result);
      setError(null);
      return result;
    } catch (err) {
      const apiError = err instanceof ApiError ? err : new ApiError(
        err instanceof Error ? err.message : String(err)
      );
      setError(apiError);
      setData(null);
      return null;
    } finally {
      setIsLoading(false);
      setIsRetrying(false);
    }
  }, [options]);

  const retry = useCallback(async (): Promise<T | null> => {
    if (!lastOperationRef.current) {
      return null;
    }
    return execute(lastOperationRef.current);
  }, [execute]);

  const abort = useCallback(() => {
    retryHandlerRef.current?.abort();
    setIsLoading(false);
    setIsRetrying(false);
  }, []);

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setIsLoading(false);
    setIsRetrying(false);
    setRetryCount(0);
    lastOperationRef.current = null;
  }, []);

  return {
    data,
    error,
    isLoading,
    isRetrying,
    retryCount,
    execute,
    retry,
    abort,
    reset
  };
}

// Network status monitor
export class NetworkMonitor {
  private static instance: NetworkMonitor;
  private isOnline: boolean = navigator.onLine;
  private listeners: Set<(online: boolean) => void> = new Set();

  private constructor() {
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
  }

  static getInstance(): NetworkMonitor {
    if (!NetworkMonitor.instance) {
      NetworkMonitor.instance = new NetworkMonitor();
    }
    return NetworkMonitor.instance;
  }

  private handleOnline = () => {
    this.isOnline = true;
    this.notifyListeners();
  };

  private handleOffline = () => {
    this.isOnline = false;
    this.notifyListeners();
  };

  private notifyListeners() {
    this.listeners.forEach(listener => listener(this.isOnline));
  }

  subscribe(listener: (online: boolean) => void): () => void {
    this.listeners.add(listener);
    // Return unsubscribe function
    return () => this.listeners.delete(listener);
  }

  getStatus(): boolean {
    return this.isOnline;
  }

  async waitForConnection(timeout: number = 30000): Promise<void> {
    if (this.isOnline) return;

    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        unsubscribe();
        reject(new ApiError('Bağlantı zaman aşımına uğradı', -4));
      }, timeout);

      const unsubscribe = this.subscribe((online) => {
        if (online) {
          clearTimeout(timeoutId);
          unsubscribe();
          resolve();
        }
      });
    });
  }
}